import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { adminNotifications } from "@/mocks/admin";
import { newsArticles } from "@/mocks/news";
import { videos } from "@/mocks/videos";
import { programs } from "@/mocks/programs";

interface SearchResult {
  group: string;
  icon: string;
  label: string;
  path: string;
}

export default function Topbar({ onMenuClick }: { onMenuClick: () => void }) {
  const { user, logout } = useAdminAuth();
  const navigate = useNavigate();

  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [notifications, setNotifications] = useState(adminNotifications);

  const searchRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);

  const unreadCount = notifications.filter((n) => !n.read).length;

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
      if (bellRef.current && !bellRef.current.contains(e.target as Node)) {
        setBellOpen(false);
      }
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const results = useMemo<SearchResult[]>(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    const out: SearchResult[] = [];
    newsArticles
      .filter((a) => a.headline.toLowerCase().includes(q) || a.category.toLowerCase().includes(q))
      .slice(0, 4)
      .forEach((a) => out.push({ group: "Articles", icon: "ri-newspaper-line", label: a.headline, path: "/admin/news" }));
    videos
      .filter((v) => v.title.toLowerCase().includes(q) || v.category.toLowerCase().includes(q))
      .slice(0, 4)
      .forEach((v) => out.push({ group: "Videos", icon: "ri-movie-line", label: v.title, path: "/admin/videos" }));
    programs
      .filter((p) => p.name.toLowerCase().includes(q) || p.category.toLowerCase().includes(q))
      .slice(0, 4)
      .forEach((p) => out.push({ group: "Programs", icon: "ri-tv-line", label: p.name, path: "/admin/programs" }));
    return out;
  }, [query]);

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const markAllRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  return (
    <header className="sticky top-0 z-20 flex items-center gap-3 h-16 px-4 md:px-6 bg-white border-b border-background-200">
      <button
        type="button"
        onClick={onMenuClick}
        className="lg:hidden w-9 h-9 flex items-center justify-center rounded-md hover:bg-background-100 text-foreground-700"
        aria-label="Open menu"
      >
        <i className="ri-menu-line text-xl" />
      </button>

      <div className="flex items-center gap-2">
        <span className="w-7 h-7 flex items-center justify-center rounded-md bg-accent-500 text-white font-extrabold text-xs">
          L+
        </span>
        <span className="hidden sm:inline text-sm font-semibold text-foreground-900">Administration</span>
      </div>

      <div className="flex-1" />

      <div ref={searchRef} className="relative hidden md:block w-72">
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400">
            <i className="ri-search-line text-sm" />
          </span>
          <input
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSearchOpen(true);
            }}
            onFocus={() => setSearchOpen(true)}
            placeholder="Search articles, videos, programs…"
            className="w-full pl-9 pr-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
          />
        </div>

        {searchOpen && query.trim().length >= 2 && (
          <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-background-200 rounded-lg overflow-hidden shadow-lg z-50">
            {results.length === 0 ? (
              <p className="px-4 py-3 text-sm text-foreground-500">No results found for “{query}”.</p>
            ) : (
              <ul className="max-h-80 overflow-y-auto py-1">
                {results.map((r, i) => (
                  <li key={i}>
                    <button
                      type="button"
                      onClick={() => {
                        setSearchOpen(false);
                        setQuery("");
                        navigate(r.path);
                      }}
                      className="w-full flex items-start gap-3 px-4 py-2.5 text-left hover:bg-background-50"
                    >
                      <span className="w-5 h-5 flex items-center justify-center text-foreground-400 shrink-0 mt-0.5">
                        <i className={`${r.icon} text-sm`} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm text-foreground-900 truncate">{r.label}</span>
                        <span className="block text-[11px] text-foreground-500">{r.group}</span>
                      </span>
                      <span className="text-xs text-primary-600 whitespace-nowrap">Edit</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <a
        href="/"
        target="_blank"
        rel="noreferrer"
        className="hidden sm:inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-primary-700 border border-primary-200 rounded-md hover:bg-primary-50 whitespace-nowrap"
      >
        <span className="w-4 h-4 flex items-center justify-center">
          <i className="ri-external-link-line text-sm" />
        </span>
        View Website
      </a>

      <div ref={bellRef} className="relative">
        <button
          type="button"
          onClick={() => setBellOpen((v) => !v)}
          className="relative w-9 h-9 flex items-center justify-center rounded-md hover:bg-background-100 text-foreground-700"
          aria-label="Notifications"
        >
          <i className="ri-notification-3-line text-xl" />
          {unreadCount > 0 && (
            <span className="absolute top-1 right-1 w-4 h-4 flex items-center justify-center rounded-full bg-accent-500 text-white text-[10px] font-bold">
              {unreadCount}
            </span>
          )}
        </button>

        {bellOpen && (
          <div className="absolute right-0 top-full mt-1 w-80 bg-white border border-background-200 rounded-lg overflow-hidden shadow-lg z-50">
            <div className="flex items-center justify-between px-4 py-3 border-b border-background-100">
              <p className="text-sm font-semibold text-foreground-900">Notifications</p>
              <button
                type="button"
                onClick={markAllRead}
                className="text-xs text-primary-600 hover:underline whitespace-nowrap"
              >
                Mark all read
              </button>
            </div>
            <ul className="max-h-96 overflow-y-auto">
              {notifications.map((n) => (
                <li
                  key={n.id}
                  className={`flex items-start gap-3 px-4 py-3 border-b border-background-50 ${
                    n.read ? "opacity-70" : "bg-primary-50/40"
                  }`}
                >
                  <span className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 shrink-0">
                    <i className="ri-megaphone-line text-sm" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground-900">{n.title}</p>
                    <p className="text-xs text-foreground-600 mt-0.5">{n.message}</p>
                    <p className="text-[11px] text-foreground-400 mt-1">{n.time}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div ref={profileRef} className="relative">
        <button
          type="button"
          onClick={() => setProfileOpen((v) => !v)}
          className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-md hover:bg-background-100"
        >
          <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-white text-xs font-bold">
            {user?.initials ?? "L+"}
          </span>
          <span className="hidden sm:block text-sm font-medium text-foreground-800">
            {user?.name ?? "Administrator"}
          </span>
          <i className="ri-arrow-down-s-line text-sm text-foreground-500" />
        </button>

        {profileOpen && (
          <div className="absolute right-0 top-full mt-1 w-52 bg-white border border-background-200 rounded-lg overflow-hidden shadow-lg z-50 py-1">
            <div className="px-4 py-2 border-b border-background-100">
              <p className="text-sm font-semibold text-foreground-900 truncate">{user?.name}</p>
              <p className="text-[11px] text-foreground-500">{user?.role}</p>
            </div>
            {[
              { label: "My Profile", icon: "ri-user-line" },
              { label: "Account Settings", icon: "ri-settings-3-line" },
              { label: "Notifications", icon: "ri-notification-3-line" },
              { label: "Security", icon: "ri-shield-check-line" },
            ].map((item) => (
              <button
                key={item.label}
                type="button"
                onClick={() => setProfileOpen(false)}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-foreground-700 hover:bg-background-50 text-left"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`${item.icon} text-sm`} />
                </span>
                {item.label}
              </button>
            ))}
            <div className="border-t border-background-100 mt-1 pt-1">
              <button
                type="button"
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-accent-600 hover:bg-accent-50 text-left"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-logout-box-r-line text-sm" />
                </span>
                Logout
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}